/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { PriceComponent } from '@msdyn365-commerce/components';
import { TenderLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
import { IPaymentMethods, IPaymentMethodsInput } from '../../../common';

// tslint:disable-next-line:max-func-body-length
export const getOrderDetailsPaymentMethods = ({
    priceContext,
    tenderLines,
    tenderIdOperationIdMap,
    resource: { paymentMethodsTitle, creditCardEndingLabel, giftCardEndingLabel, amountCoveredLabel, loyaltyCardUsedLabel, cashUsedLabel, customerAccountUsedLabel }
}: IPaymentMethodsInput): IPaymentMethods => {

    const OPERATIONS = {
        PayCard: 201,
        PayGiftCertificate: 214,
        PayLoyalty: 207,
        PayCash: 200,
        PayCustomerAccount: 202,
    };

    const getLastFourDigit = (digits = '') => digits.substr(-4);

    const getCard = (tenderLine: TenderLine, showAmount: boolean = false) => {
        const cardLastFourDigit = getLastFourDigit(tenderLine.MaskedCardNumber);
        // @ts-ignore
        const isValidCardNumber = cardLastFourDigit && !isNaN(cardLastFourDigit);
        const amount = (tenderLine.AuthorizedAmount || 0) > 0 ? tenderLine.AuthorizedAmount : tenderLine.Amount;
        const showAmountCovered = showAmount && amount && amount > 0 ? true : false;

        return (
            <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
                <p className='ms-order-details__payment-methods-card'>
                    {`${tenderLine.CardTypeId} ${isValidCardNumber ? `${creditCardEndingLabel} ${cardLastFourDigit}` : ``}`}
                </p>
                {showAmountCovered && <p className='ms-order-details__payment-methods-card-amount-info'>
                    {amountCoveredLabel}
                    <PriceComponent
                        {...priceContext}
                        className='ms-order-details__payment-methods-card-amount'
                        data={{ price: { CustomerContextualPrice: amount || 0 } }}
                    />
                </p>}
            </div>
        );
    };

    const getCustomerAccountPayment = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-customer-account'>
                {customerAccountUsedLabel} {tenderLine.CustomerId}
            </p>
            <p className='ms-order-details__payment-methods-customer-account-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-customer-account-amount'
                    data={{ price: { CustomerContextualPrice: tenderLine.Amount || 0 } }}
                />
            </p>
        </div>
    );

    const getGiftCertificate = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-gift-card-number'>
                {giftCardEndingLabel} {getLastFourDigit(tenderLine.GiftCardId)}
            </p>
            <p className='ms-order-details__payment-methods-gift-card-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-gift-card-amount'
                    data={{ price: { CustomerContextualPrice: tenderLine.Amount || 0 } }}
                />
            </p>
        </div>
    );

    const getLoyalty = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-loyalty-number'>
                {loyaltyCardUsedLabel} {tenderLine.LoyaltyCardId}
            </p>
            <p className='ms-order-details__payment-methods-loyalty-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-loyalty-amount'
                    data={{ price: { CustomerContextualPrice: tenderLine.Amount || 0 } }}
                />
            </p>
        </div>
    );

    const getCash = (tenderLine: TenderLine) => (
        <div className='ms-order-details__payment-methods-line' key={tenderLine.TenderTypeId}>
            <p className='ms-order-details__payment-methods-cash'>{cashUsedLabel}</p>
            <p className='ms-order-details__payment-methods-cash-amount-info'>
                {amountCoveredLabel}
                <PriceComponent
                    {...priceContext}
                    className='ms-order-details__payment-methods-cash-amount'
                    data={{ price: { CustomerContextualPrice: tenderLine.Amount || 0 } }}
                />
            </p>
        </div>
    );

    return {
        paymentMethodsProps: { className: 'ms-order-details__payment-methods' },
        title: <div className='ms-order-details__payment-methods-title'>{paymentMethodsTitle}</div>,
        methods: tenderLines.reduce((lines: React.ReactNode[], tenderLine) => {
            if (!tenderLine.TenderTypeId) {
                return lines;
            }
            switch (tenderIdOperationIdMap[tenderLine.TenderTypeId]) {
                case OPERATIONS.PayCard:
                    lines.push(getCard(tenderLine, tenderLines.length > 1));
                    break;
                case OPERATIONS.PayGiftCertificate:
                    lines.push(getGiftCertificate(tenderLine));
                    break;
                case OPERATIONS.PayLoyalty:
                    lines.push(getLoyalty(tenderLine));
                    break;
                case OPERATIONS.PayCash:
                    lines.push(getCash(tenderLine));
                    break;
                case OPERATIONS.PayCustomerAccount:
                    lines.push(getCustomerAccountPayment(tenderLine));
                    break;
                default:
                // do nothing
            }
            return lines;
        },                          [])
    };
};
